"use client";

import { useMemo } from "react";
import {
    gridItemVariants,
    slideFromRightVariants,
    staggerContainerVariants
} from "@/lib/animations";
import { motion } from "framer-motion";
import Link from "next/link";
import NextImage from '@/components/ui/MediaImage';
import BlogSearchDropdown from "@/components/ui/BlogSearchDropdown";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type {
    BlogPostCardViewModel,
    BlogSidebarCategory,
    BlogSidebarTag,
    PageUrlMap
} from "@/types/blog";

export interface BlogRightBarProps {
    recentPosts: BlogPostCardViewModel[];
    categories: BlogSidebarCategory[];
    tags: BlogSidebarTag[];
    language: string;
    defaultLanguage: string;
    searchUrl?: string | null;
    translations?: Record<string, string>; // Deprecated: useLanguageVariables kullanılıyor, geriye uyumluluk için bırakıldı
    pageUrls?: PageUrlMap;
}

export default function BlogRightBar({
    recentPosts = [],
    categories = [],
    tags = [],
    language,
    defaultLanguage,
    translations, // Deprecated: geriye uyumluluk için bırakıldı
    pageUrls,
}: BlogRightBarProps) {
    const { getVariable } = useLanguageVariables();

    return (
        <motion.div
            className="col-span-12 lg:col-span-4 self-start"
            variants={slideFromRightVariants}
        >
            <motion.div
                className="flex flex-col gap-7"
                variants={staggerContainerVariants}
            >
                <motion.div
                    className="bg-[#F4F6F8] px-7 py-10"
                    variants={gridItemVariants}
                >
                    <h3 className="flex font-semibold text-alsero-dark-gray text-2xl border-l-[2px] border-alsero-first pl-3 mb-6">
                        {getVariable('blog_right_bar.search_title', 'Search Here')}
                    </h3>
                    <BlogSearchDropdown
                        language={language}
                        defaultLanguage={defaultLanguage}
                        pageUrls={pageUrls}
                        translations={translations}
                    />
                </motion.div>
                <motion.div
                    className="bg-[#F4F6F8] px-7 py-10"
                    variants={gridItemVariants}
                >
                    <h3 className="flex font-semibold text-alsero-dark-gray text-2xl border-l-[2px] border-alsero-first pl-3 mb-6">
                        {getVariable('blog_right_bar.recent_title', 'Recent News')}
                    </h3>
                    <ul className="flex flex-col gap-7">
                        {recentPosts.length === 0 && (
                            <li className="text-sm text-alsero-light-gray">
                                {getVariable('blog_right_bar.empty_recent', 'No entries yet.')}
                            </li>
                        )}
                        {recentPosts.map((post, index) => (
                            <motion.li
                                key={post.id ?? `${post.href}-${index}`}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ x: 5 }}
                            >
                                <Link
                                    href={post.href}
                                    className="flex flex-row gap-5 pb-2 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[1px] after:bg-alsero-first after:w-0 hover:after:w-full after:transition-all after:duration-300 after:ease-in-out"
                                >
                                    <div className="w-[102px] h-[84px] shrink-0">
                                        <NextImage
                                            src={post.src}
                                            alt={post.alt}
                                            width={102}
                                            height={84}
                                            className="w-full h-full object-cover shrink"
                                            unoptimized={post.src.startsWith('/api/media/proxy/')}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-2 w-full">
                                        <span className="text-sm font-medium text-alsero-light-gray">{post.date}</span>
                                        <h2 className="text-lg font-medium text-alsero-dark-gray text-wrap">{post.title}</h2>
                                    </div>
                                </Link>
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>
                <motion.div
                    className="bg-[#F4F6F8] px-7 py-10"
                    variants={gridItemVariants}
                >
                    <h3 className="flex font-semibold text-alsero-dark-gray text-2xl border-l-[2px] border-alsero-first pl-3 mb-6">
                        {getVariable('blog_right_bar.categories_title', 'Categories')}
                    </h3>
                    <ul className="flex flex-col gap-7">
                        {categories.length === 0 && (
                            <li className="text-sm text-alsero-light-gray">
                                {getVariable('blog_right_bar.empty_categories', 'No categories available.')}
                            </li>
                        )}
                        {categories.map((cat, index) => (
                            <motion.li
                                key={`${cat.link}-${index}`}
                                className="group px-5 py-4 bg-white transition-colors ease-in-out duration-300 hover:bg-alsero-first border border-[#EDEDED] hover:border-alsero-first"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.03, x: 5 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Link
                                    href={cat.link}
                                    className="flex flex-row justify-between text-alsero-dark-gray font-medium text-[15px] group-hover:text-white"
                                >
                                    <h2>{cat.title}</h2>
                                    {typeof cat.count === "number" && (
                                        <span>{cat.count}</span>
                                    )}
                                </Link>
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>
                <motion.div
                    className="bg-[#F4F6F8] px-7 py-10"
                    variants={gridItemVariants}
                >
                    <h3 className="flex font-semibold text-alsero-dark-gray text-2xl border-l-[2px] border-alsero-first pl-3 mb-6">
                        {getVariable('blog_right_bar.tags_title', 'Popular Tags')}
                    </h3>
                    <ul className="grid grid-cols-12 gap-3">
                        {tags.length === 0 && (
                            <li className="col-span-12 text-sm text-alsero-light-gray">
                                {getVariable('blog_right_bar.empty_tags', 'No tags available.')}
                            </li>
                        )}
                        {tags.map((tag, index) => (
                            <motion.li
                                key={`${tag.link}-${index}`}
                                className="col-span-4 group px-5 py-4 bg-white transition-colors ease-in-out duration-300 hover:bg-alsero-first border border-[#EDEDED] hover:border-alsero-first"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.05 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Link
                                    href={tag.link}
                                    className="text-center text-alsero-dark-gray font-medium text-[15px] group-hover:text-white"
                                >
                                    <h2>{tag.title}</h2>
                                </Link>
                            </motion.li>
                        ))}
                    </ul>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}